import { useState } from "react";
import { motion } from "framer-motion";
import { Mail, Phone, ArrowRight } from "lucide-react";
import Images from "../../../utils/Images";
import { Link, useNavigate, useLocation } from "react-router-dom";

export default function SignIn() {
  const [method, setMethod] = useState("email");
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || "/";

  // Route-only page; inputs live in method-specific pages

  return (
    <div className="grid grid-cols-1 min-h-screen lg:grid-cols-2">
      {/* Left hero panel */}
      <motion.div
        className="hidden relative w-full h-full lg:block"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1 }}
      >
        <img
          src={`https://images.unsplash.com/photo-1560264418-c4445382edbc?q=80&w=1470&auto=format&fit=crop`}
          alt="trip-secure-signin"
          className="object-cover w-full h-full"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/40 to-black/80" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/30" />
        <div className="flex absolute inset-0 flex-col justify-between p-12">
          <motion.div
            initial={{ y: -50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{
              duration: 0.8,
              type: "spring",
              bounce: 0.4,
            }}
            className="text-white"
          >
            <motion.h3
              initial={{ x: -20, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="mb-3 text-2xl font-light"
            >
              Welcome to
            </motion.h3>
            <motion.h2
              initial={{ x: -20, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="text-4xl font-bold bg-gradient-to-r from-[#FF6C6C] to-white bg-clip-text text-transparent"
            >
              <Link to="/" className="block cursor-pointer group">
              <img
                src={Images.logo_navbar}
                alt="trip-secure"
                className="h-10"
              />
            </Link>
            </motion.h2>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1, duration: 0.8 }}
            className="space-y-6"
          >
            <motion.div
              whileHover={{ scale: 1.02 }}
              transition={{ type: "spring", stiffness: 300 }}
              className="p-6 bg-gradient-to-r rounded-xl border backdrop-blur-md from-black/40 to-black/20 border-white/10"
            >
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.2 }}
                className="mb-4 text-lg italic text-white/90"
              >
                "Travel far, explore freely, and leave the worries behind—our
                trip insurance has you covered every step of the way."
              </motion.p>
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 1.4 }}
                className="flex gap-4 items-center"
              >
                <div className="flex -space-x-3">
                  {[1, 2, 3].map((i) => (
                    <motion.div
                      key={i}
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ delay: 1.4 + i * 0.2 }}
                      className="w-10 h-10 rounded-full border-2 backdrop-blur-sm border-[#05E881]/20 bg-black/30"
                    />
                  ))}
                </div>
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 2 }}
                  className="text-sm text-white/80"
                >
                  Join our{" "}
                  <span className="text-[#05E881] font-bold">100+</span> members
                </motion.p>
              </motion.div>
            </motion.div>

            <motion.div
              initial="hidden"
              animate="visible"
              variants={{
                visible: {
                  transition: {
                    staggerChildren: 0.2,
                  },
                },
              }}
              className="grid grid-cols-3 gap-4"
            >
              {[
                { number: "100,000k+", label: "Policies Issued" },
                { number: "300+", label: "Registered Insurance Providers" },
                { number: "GHS 1 million+", label: "in claims paid" },
              ].map((stat, index) => (
                <motion.div
                  key={index}
                  variants={{
                    hidden: { opacity: 0, y: 20 },
                    visible: { opacity: 1, y: 0 },
                  }}
                  whileHover={{
                    scale: 1.05,
                    backgroundColor: "rgba(0, 0, 0, 0.4)",
                  }}
                  className="p-4 rounded-lg border backdrop-blur-sm transition-all duration-300 bg-black/30 border-white/10"
                >
                  <motion.h4
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 2 + index * 0.2 }}
                    className="mb-1 text-xl font-bold text-[#05E881]"
                  >
                    {stat.number}
                  </motion.h4>
                  <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 2.2 + index * 0.2 }}
                    className="text-sm text-white/70"
                  >
                    {stat.label}
                  </motion.p>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </motion.div>

      {/* Right auth form */}
      <div className="flex justify-center items-center p-6 lg:p-12">
        <div className="w-full max-w-md">
          <div className="flex justify-center mb-3">
            <img src={Images.logo_colored} alt="TripSecure" className="h-20" />
          </div>
          <h1 className="text-2xl font-bold text-center">Welcome Back</h1>
          <p className="mt-1 text-sm text-center text-gray-600">
            Join us to Secure Your Journey—Get Covered in Minutes.
          </p>

          <div className="mt-6 space-y-3">
            <button
              onClick={() => setMethod("email")}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg border transition ${
                method === "email"
                  ? "border-emerald-500 bg-emerald-50"
                  : "border-gray-300"
              }`}
            >
              <Mail size={18} /> Sign in with Email
            </button>
            <button
              onClick={() => setMethod("phone")}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg border transition ${
                method === "phone"
                  ? "border-emerald-500 bg-emerald-50"
                  : "border-gray-300"
              }`}
            >
              <Phone size={18} /> Sign in with phone number
            </button>
          </div>

          <div className="mt-6 space-y-3">
            <button
              onClick={() => {
                const target = method === 'email' ? '/signin/email' : '/signin/phone';
                navigate(target, { state: { from } });
              }}
              className="inline-flex gap-2 justify-center items-center px-4 py-3 w-full font-semibold text-white bg-emerald-500 rounded-lg shadow hover:bg-emerald-600"
            >
              Continue with {method === 'email' ? 'Email' : 'Phone'} <ArrowRight size={16} />
            </button>
          </div>

          <p className="mt-4 text-xs text-center text-gray-600">
            Already have an account?{" "}
            <Link to="/signup" className="text-emerald-600">
              Register
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
